import ColumnConfigurationIcon from '@/assets/icons/ColumnConfigurationIcon'
import DeleteIcon from '@/assets/icons/DeleteIcon'
import DownloadIcon from '@/assets/icons/DownloadIcon'
import EditIcon from '@/assets/icons/EditIcon'
import CloseIcon from '@/assets/icons/MTCancelIcon'
import SaveIcon from '@/assets/icons/MTSaveIcon'
import SearchIconBtn from '@/assets/icons/SearchIconBtn'
import { ConfirmationDialog } from '@/components/common'
import ColumnConfigurationBase from '@/components/common/columnConfiguration'
import Search from '@/components/common/Search'
import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { useDefineDataType } from '@/hooks/useDefineDataType'
import { MTCommonTable } from '@/multi-territory/mt-components/mt-shared/MTCommonTable'
import { useMTUnsavedChangesGuard } from '@/multi-territory/mt-hooks/useMTUnsavedChangesGuard'
import {
    bulkDeleteServiceMasterRecords,
    bulkEditServiceMasterRecords,
    downloadServiceMaster,
    fetchServiceMasterList,
    fetchServiceMasterMetadata,
    metadataToTableColumns,
    triggerBlobDownload,
} from '@/multi-territory/mt-services/mt-service-master-api'
import type {
    MTMetadataTableColumn,
    MTServiceMasterRecord,
} from '@/multi-territory/mt-types/mt-metadata'
import { formatMTHeaderLabel } from '@/multi-territory/mt-utils/mt-search-fields'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

const PayeeMasterSearch = Search as any
const ColumnConfiguration = ColumnConfigurationBase as any

/**
 * MTPayeeMasterTable - Metadata-driven Payee Master table with View/Edit/Delete/Download/Search.
 *
 * APIs integrated:
 * - GET  /data/{countryCode}/PAYEE_MASTER/metadata (column config)
 * - POST /payee-master/list (paginated list with search)
 * - PUT  /payee-master/{uuid} (update)
 * - DELETE /payee-master/{uuid} (delete)
 * - POST /payee-master/download (Excel export)
 */
export function MTPayeeMasterTable() {
  // Derive dataType from navigation API instead of hardcoding
  const { dataType, dataTypeLower } = useDefineDataType('PAYEE_MASTER')
  const DATA_TYPE_UPPER = dataType ?? 'PAYEE_MASTER'
  const DATA_TYPE_LOWER = dataTypeLower ?? 'payee-master'

  const [columns, setColumns] = useState<MTMetadataTableColumn[]>([])
  const [data, setData] = useState<MTServiceMasterRecord[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    GLOBAL_CONSTANTS.DEFAULT_PAGE_SIZE || 100
  )
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [editMode, setEditMode] = useState(false)
  const [editingRowId, setEditingRowId] = useState<string | null>(null)
  const [editingData, setEditingData] = useState<Record<string, any>>({})
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const searchIconRef = useRef<HTMLButtonElement>(null)
  const [searchCOList, setSearchCOList] = useState<any[]>([])
  const [searchCriteria, setSearchCriteria] = useState<any[]>([
    { id: 1, logicalOperator: 'Where', field: '', value: '' },
  ])
  const [downloading, setDownloading] = useState(false)
  const [showTable, setShowTable] = useState(false)
  const [rawMetadataFields, setRawMetadataFields] = useState<any[]>([])
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [columnConfigOpen, setColumnConfigOpen] = useState(false)
  const [columnConfigAnchor, setColumnConfigAnchor] =
    useState<HTMLElement | null>(null)
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])

  const { showSnackbar } = useSnackbar()
  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country.countryCode
  )
  const selectedEntityUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedEntityUuid ?? ''
  )
  const selectedTanUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedTanUuid ?? ''
  )
  const entityMetaByUuid = useSelector(
    (state: any) => state.mtEntityTan?.entityMetaByUuid ?? {}
  )
  const selectedEntityMeta = entityMetaByUuid[selectedEntityUuid] || null
  const tanApplicable = selectedEntityMeta?.tanApplicable ?? true
  const apiIdentifierUuid = tanApplicable ? selectedTanUuid : selectedEntityUuid
  const lastFetchedKeyRef = useRef('')

  // Hide table when entity or TAN selection changes (user must click View again)
  const prevEntityRef = useRef(selectedEntityUuid)
  const prevTanRef = useRef(selectedTanUuid)
  useEffect(() => {
    if (prevEntityRef.current && prevEntityRef.current !== selectedEntityUuid) {
      setShowTable(false)
      setData([])
      setSelectedItems([])
      setEditMode(false)
      setEditingData({})
      lastFetchedKeyRef.current = ''
    }
    prevEntityRef.current = selectedEntityUuid
  }, [selectedEntityUuid])

  useEffect(() => {
    if (prevTanRef.current && prevTanRef.current !== selectedTanUuid) {
      setShowTable(false)
      setData([])
      setSelectedItems([])
      setEditMode(false)
      setEditingData({})
      lastFetchedKeyRef.current = ''
    }
    prevTanRef.current = selectedTanUuid
  }, [selectedTanUuid])

  const loadMetadata = useCallback(async () => {
    if (!countryCode || !DATA_TYPE_UPPER) return
    const metaResult = await fetchServiceMasterMetadata(
      countryCode,
      DATA_TYPE_UPPER
    )
    if (metaResult.success) {
      setColumns(metadataToTableColumns(metaResult.data.fields))
      setRawMetadataFields(metaResult.data.fields)
    }
  }, [countryCode, DATA_TYPE_UPPER])

  useEffect(() => {
    loadMetadata()
  }, [loadMetadata])

  const buildRowUuid = (row: any, idx: number) =>
    row.associated_uuid || row.global_uuid || row.uuid || row.id || `row-${idx}`

  const loadData = useCallback(
    async (searchList?: any[]) => {
      if (!countryCode || !apiIdentifierUuid || !DATA_TYPE_LOWER) return
      setLoading(true)
      const payload = {
        pageFilter: {
          currentPage,
          pageSize: itemsPerPage,
          sortField: 'payee_name',
          sortOrder: 'ASC' as const,
        },
        searchCOList: searchList ?? searchCOList,
      }
      const dataResult = await fetchServiceMasterList(
        countryCode,
        apiIdentifierUuid,
        payload,
        DATA_TYPE_LOWER,
        { tanApplicable }
      )
      if (dataResult.success) {
        setData(
          dataResult.data.content.map((row: any, idx: number) => ({
            ...row,
            uuid: buildRowUuid(row, idx),
          }))
        )
        setTotalItems(dataResult.data.totalElements)
      } else {
        showSnackbar(dataResult.error, 'error')
      }
      setLoading(false)
    },
    [
      countryCode,
      apiIdentifierUuid,
      currentPage,
      itemsPerPage,
      searchCOList,
      showSnackbar,
      tanApplicable,
    ]
  )

  useEffect(() => {
    if (showTable && apiIdentifierUuid && countryCode) loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage])

  const viewTrigger = useSelector(
    (state: any) => state.mtEntityTan?.viewTrigger ?? 0
  )
  const lastViewTriggerRef = useRef(viewTrigger)

  useEffect(() => {
    if (viewTrigger > 0 && apiIdentifierUuid && countryCode) {
      if (viewTrigger <= lastViewTriggerRef.current) return
      lastViewTriggerRef.current = viewTrigger
      const fetchKey = selectedEntityUuid + '|' + selectedTanUuid
      if (showTable && lastFetchedKeyRef.current === fetchKey) return
      setShowTable(true)
      setLoading(true)
      const payload = {
        pageFilter: {
          currentPage,
          pageSize: itemsPerPage,
          sortField: 'payee_name',
          sortOrder: 'ASC' as const,
        },
        searchCOList,
      }
      fetchServiceMasterList(
        countryCode,
        apiIdentifierUuid,
        payload,
        DATA_TYPE_LOWER,
        { tanApplicable }
      ).then(dataResult => {
        if (dataResult.success) {
          lastFetchedKeyRef.current = fetchKey
          setData(
            dataResult.data.content.map((row: any, idx: number) => ({
              ...row,
              uuid: buildRowUuid(row, idx),
            }))
          )
          setTotalItems(dataResult.data.totalElements)
        } else {
          showSnackbar(dataResult.error, 'error')
        }
        setLoading(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewTrigger])

  const allVisibleColumns = useMemo(
    () => columns.filter(col => !col.id.startsWith('sys_')),
    [columns]
  )

  const visibleColumns = useMemo(
    () => allVisibleColumns.filter(col => !hiddenColumns.includes(col.id)),
    [allVisibleColumns, hiddenColumns]
  )

  // Column configuration (columns list for the config popover)
  const columnConfigColumns = useMemo(
    () => allVisibleColumns.map(col => ({ id: col.id, label: col.label })),
    [allVisibleColumns]
  )

  const columnConfigVisibleIds = useMemo(
    () =>
      allVisibleColumns
        .filter(col => !hiddenColumns.includes(col.id))
        .map(col => col.id),
    [allVisibleColumns, hiddenColumns]
  )

  const handleColumnConfigOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnConfigAnchor(event.currentTarget)
    setColumnConfigOpen(!columnConfigOpen)
  }

  const handleColumnConfigClose = () => {
    setColumnConfigOpen(false)
    setColumnConfigAnchor(null)
  }

  const handleColumnChange = (selectedColumns: string[]) => {
    const newHidden = allVisibleColumns
      .filter(col => !selectedColumns.includes(col.id))
      .map(col => col.id)
    setHiddenColumns(newHidden)
  }

  const searchFields = useMemo(
    () =>
      visibleColumns.map(col => {
        // Match the advance-search label to the table header column exactly
        // (sentence case / "running text", acronyms such as ID/TIN uppercase).
        const label = formatMTHeaderLabel(col.label)
        return {
          id: col.id,
          label,
          type:
            col.type === 'date'
              ? 'date'
              : col.type === 'dropdown'
                ? 'dropdown'
                : 'text',
          placeholder:
            col.type === 'date'
              ? 'DD/MM/YYYY'
              : col.type === 'dropdown'
                ? `Select ${label}`
                : `Enter ${label}`,
          ...(col.type === 'dropdown' ? { options: col.options ?? [] } : {}),
        }
      }),
    [visibleColumns]
  )

  const handleSelectItem = (uuid: string) => {
    setSelectedItems(p =>
      p.includes(uuid) ? p.filter(id => id !== uuid) : [...p, uuid]
    )
  }
  const handleSelectAll = () => {
    setSelectedItems(p =>
      p.length === data.length ? [] : data.map(r => r.uuid as string)
    )
  }

  const handleEditClick = () => {
    setEditMode(true)
    setEditingRowId(null)
    setEditingData({})
  }

  const handleFieldChange = (
    rowId: string,
    fieldKey: string,
    value: string
  ) => {
    setEditingData(prev => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [fieldKey]: value,
      },
    }))
  }

  const validateRowData = (
    rowUuid: string,
    changes: Record<string, any>
  ): string | null => {
    const originalRow = data.find(
      r => (r.uuid || r.associated_uuid || r.global_uuid) === rowUuid
    )
    if (!originalRow) return 'Row not found on current page'
    const mergedRow = { ...originalRow, ...changes }
    for (const field of rawMetadataFields) {
      if (!field.editable || !field.visible) continue
      const value = String(mergedRow[field.field_key] ?? '').trim()
      if (field.required && (!value || value === '-')) {
        return (
          field.validations?.required_message || `${field.label} is required`
        )
      }
      // Conditional required: e.g., "Other Foreign Payee type" is required
      // when "Payee's Business Structure" = "OTHERS: PLEASE SPECIFY"
      if (
        field.validations?.conditionallyRequired &&
        (!value || value === '-')
      ) {
        const businessStructureValue = String(
          mergedRow['payees_business_structure'] ?? ''
        )
          .trim()
          .toUpperCase()
        if (businessStructureValue === 'OTHERS: PLEASE SPECIFY') {
          return `${field.label} is required when Payee's Business Structure is "Others: Please Specify"`
        }
      }
      if (!value || value === '-') continue
      if (field.type === 'number') {
        const numVal = parseFloat(value)
        if (isNaN(numVal)) return `${field.label} must be a valid number`
        if (
          field.validations?.min !== undefined &&
          numVal < field.validations.min
        ) {
          return `${field.label} must be at least ${field.validations.min}`
        }
        if (
          field.validations?.max !== undefined &&
          numVal > field.validations.max
        ) {
          return `${field.label} must not exceed ${field.validations.max}`
        }
      }
      if (
        field.validations?.maxLength &&
        value.length > field.validations.maxLength
      ) {
        return `${field.label} exceeds max length ${field.validations.maxLength}`
      }
      if (
        field.validations?.regex &&
        field.type !== 'dropdown' &&
        field.type !== 'date'
      ) {
        try {
          if (!new RegExp(field.validations.regex).test(value)) {
            return (
              field.validations.regex_message ||
              `${field.label} has invalid format`
            )
          }
        } catch {
          /* skip */
        }
      }
    }
    return null
  }

  const handleSaveEdit = async () => {
    const entries = Object.entries(editingData || {})
    if (entries.length === 0) {
      showSnackbar('No changes to save', 'warning')
      return
    }

    const invalidMessages: string[] = []
    const validPayloads: { uuid: string; data: Record<string, any> }[] = []

    entries.forEach(([rowUuid, changes]) => {
      if (rowUuid.startsWith('row-')) {
        invalidMessages.push(
          'Cannot save: record UUID not found. Try refreshing.'
        )
        return
      }
      const error = validateRowData(rowUuid, changes as Record<string, any>)
      if (error) {
        const rowIndex = data.findIndex(
          r => (r.uuid || r.associated_uuid || r.global_uuid) === rowUuid
        )
        invalidMessages.push(`Row ${rowIndex + 1}: ${error}`)
      } else {
        const typedData: Record<string, any> = {
          ...(changes as Record<string, any>),
        }
        for (const field of rawMetadataFields) {
          if (
            field.type === 'number' &&
            typedData[field.field_key] !== undefined
          ) {
            const raw = String(typedData[field.field_key] ?? '').trim()
            typedData[field.field_key] = raw ? parseFloat(raw) : null
          }
        }
        validPayloads.push({ uuid: rowUuid, data: typedData })
      }
    })

    if (invalidMessages.length > 0) {
      invalidMessages.forEach((msg, i) => {
        setTimeout(() => showSnackbar(msg, 'error'), i * 700)
      })
      return
    }

    // Build bulk edit payload: [{uuid, ...changedFields}, ...]
    const bulkPayload = validPayloads.map(p => ({ uuid: p.uuid, ...p.data }))
    const result = await bulkEditServiceMasterRecords(
      countryCode,
      apiIdentifierUuid,
      bulkPayload,
      DATA_TYPE_LOWER,
      { tanApplicable }
    )
    if (result.success) {
      showSnackbar(result.data || 'Records updated successfully', 'success')
      handleCancelEdit()
      loadData()
    } else {
      showSnackbar(result.error || 'Failed to update records', 'error')
    }
  }

  const handleCancelEdit = () => {
    setEditMode(false)
    setEditingRowId(null)
    setEditingData({})
    setSelectedItems([])
  }

  // Unsaved-changes guard: while in edit mode, search, pagination, View and
  // navigating away ask for confirmation before discarding edits.
  const { guardUnsavedChanges, unsavedChangesDialog } =
    useMTUnsavedChangesGuard(editMode, handleCancelEdit)

  const handleDeleteClick = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Please select at least one row to delete', 'warning')
      return
    }
    setDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    setDeleteOpen(false)
    const validUuids = selectedItems.filter(uuid => !uuid.startsWith('row-'))
    if (validUuids.length === 0) {
      showSnackbar('No valid records to delete', 'error')
      return
    }
    const result = await bulkDeleteServiceMasterRecords(
      countryCode,
      apiIdentifierUuid,
      validUuids,
      DATA_TYPE_LOWER,
      { tanApplicable }
    )
    if (result.success) {
      showSnackbar(result.data || 'Records deleted successfully', 'success')
      setSelectedItems([])
      loadData()
    } else {
      showSnackbar(result.error || 'Failed to delete records', 'error')
    }
  }

  const handleDeleteCancel = () => {
    setDeleteOpen(false)
  }

  const handleDownload = async () => {
    if (!countryCode || !apiIdentifierUuid) {
      showSnackbar(
        'Country and Entity must be selected for download',
        'warning'
      )
      return
    }
    setDownloading(true)
    const result = await downloadServiceMaster(
      countryCode,
      apiIdentifierUuid,
      searchCOList.length > 0 ? { searchCOList } : undefined,
      DATA_TYPE_LOWER,
      { tanApplicable }
    )
    if (result.success) {
      triggerBlobDownload(
        result.data,
        `payee_master_${countryCode.toLowerCase()}.xlsx`
      )
      showSnackbar('Download started', 'success')
    } else {
      showSnackbar(result.error || 'Download failed', 'error')
    }
    setDownloading(false)
  }

  const openSearchDialog = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.currentTarget
    guardUnsavedChanges(() => openSearchAt(target))
  }
  const openSearchAt = (target: HTMLElement) => {
    if (
      searchCriteria.length > 0 &&
      !searchCriteria[0].field &&
      visibleColumns.length > 0
    ) {
      setSearchCriteria(prev =>
        prev.map((c, i) =>
          i === 0 ? { ...c, field: visibleColumns[0].id } : c
        )
      )
    }
    setAnchorEl(target)
    setSearchOpen(true)
  }
  const closeSearchDialog = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  const applySearch = async (searchList: any[]) => {
    // The shared Search component already emits fully-formatted criteria:
    // { fieldName, operator, value } for scalar operators and
    // { fieldName, operator, searchValueList } for the IN_LIST / NOT_IN_LIST /
    // *_PROBABLE_MATCH family (no `value` key). Store it as-is — the old
    // `.filter(i => i.value)` dropped every list-operator criterion and the
    // rebuild dropped `searchValueList`, so paginating/downloading after a list
    // search sent an empty searchCOList.
    setSearchCOList(searchList)
    setCurrentPage(1)
    setSearchCriteria(
      searchList.map((i, idx) => ({
        ...i,
        id: idx + 1,
        logicalOperator: i.logicalOperator || (idx === 0 ? 'Where' : 'And'),
      }))
    )
    setSearchOpen(false)
    lastFetchedKeyRef.current = ''
    loadData(searchList)
  }

  const resetSearch = () => {
    setSearchCOList([])
    setSearchCriteria([
      { id: 1, logicalOperator: 'Where', field: '', value: '' },
    ])
    setCurrentPage(1)
    setSearchOpen(false)
    loadData([])
  }

  const handlePageChange = (page: number) =>
    guardUnsavedChanges(() => setCurrentPage(page))
  const handleItemsPerPageChange = (size: number) =>
    guardUnsavedChanges(() => {
      setItemsPerPage(size)
      setCurrentPage(1)
    })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {showTable && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              my: '16px',
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 500,
                lineHeight: '130%',
                color: 'var(--text-heading)',
              }}
            >
              Payee details
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip title="Column Configuration">
                <IconButton
                  size="medium"
                  onClick={handleColumnConfigOpen}
                  aria-label="Column Configuration"
                >
                  <ColumnConfigurationIcon />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={downloading ? 'Downloading...' : 'Download Excel'}
              >
                <IconButton
                  size="medium"
                  onClick={handleDownload}
                  disabled={downloading}
                  aria-label="Download"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              {!editMode && (
                <Tooltip title="Edit">
                  <IconButton
                    size="medium"
                    onClick={handleEditClick}
                    aria-label="Edit"
                    sx={{ color: 'var(--forbrand-primary)' }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              {editMode && (
                <>
                  <Tooltip title="Save">
                    <IconButton
                      size="medium"
                      onClick={handleSaveEdit}
                      aria-label="Save"
                      sx={{ color: 'var(--forbrand-primary)' }}
                    >
                      <SaveIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton
                      size="medium"
                      onClick={handleCancelEdit}
                      aria-label="Cancel"
                      sx={{ color: 'var(--forbrand-primary)' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title="Search">
                <IconButton
                  size="medium"
                  ref={searchIconRef}
                  onClick={openSearchDialog}
                  aria-label="Search"
                >
                  <SearchIconBtn />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                {/* <span> so the tooltip still shows while the button is
                    disabled (no row selected) — disabled buttons don't emit
                    hover events. */}
                <span>
                  <IconButton
                    size="medium"
                    onClick={handleDeleteClick}
                    aria-label="Delete"
                    disabled={selectedItems.length === 0}
                    sx={{
                      color:
                        selectedItems.length > 0
                          ? 'var(--forbrand-error)'
                          : undefined,
                      '&.Mui-disabled': {
                        pointerEvents: 'auto',
                        color: 'var(--forbrand-primary)',
                        opacity: 0.38,
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <MTCommonTable
            columns={visibleColumns}
            data={data}
            loading={loading}
            showCheckbox
            showFooter
            selectedItems={selectedItems}
            onSelectItem={handleSelectItem}
            onSelectAll={handleSelectAll}
            editMode={editMode}
            editingRowId={editingRowId}
            editingData={editingData}
            onFieldChange={handleFieldChange}
            rawMetadataFields={rawMetadataFields}
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            emptyMessage="No payee data available"
          />

          <PayeeMasterSearch
            open={searchOpen}
            anchorEl={anchorEl}
            fields={searchFields as any}
            searchCriteria={searchCriteria}
            setSearchCriteria={setSearchCriteria}
            onClose={closeSearchDialog}
            onApply={applySearch}
            onReset={resetSearch}
            placement="bottom-start"
          />

          <ColumnConfiguration
            columns={columnConfigColumns}
            visibleColumns={columnConfigVisibleIds}
            anchorEl={columnConfigAnchor}
            open={columnConfigOpen}
            onColumnChange={handleColumnChange}
            onClose={handleColumnConfigClose}
          />

          <ConfirmationDialog
            open={deleteOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Are you sure you want to delete selected record(s)?"
            confirmText="Confirm"
            message=""
            type="delete"
            itemType=""
            showCancelButton={true}
            onCancel={handleDeleteCancel}
          />
          {unsavedChangesDialog}
        </>
      )}
    </Box>
  )
}
